var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useEffect, useState } from 'react';
import Navigation from '@components/App/Navigation';
import TagTree, { UNTAGGED } from '@components/App/TagTree';
import { api } from '../../api/client';
var DB_NAME = 'rigormining-sync';
var STORE_NAME = 'handles';
var HANDLE_KEY = 'kobo-directory';
function openHandleStore() {
    return new Promise(function (resolve, reject) {
        var req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = function () { return req.result.createObjectStore(STORE_NAME); };
        req.onsuccess = function () { return resolve(req.result); };
        req.onerror = function () { return reject(req.error); };
    });
}
function idbGet(key) {
    return __awaiter(this, void 0, void 0, function () {
        var db;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, openHandleStore()];
                case 1:
                    db = _a.sent();
                    return [2 /*return*/, new Promise(function (resolve, reject) {
                            var tx = db.transaction(STORE_NAME, 'readonly');
                            var req = tx.objectStore(STORE_NAME).get(key);
                            req.onsuccess = function () { return resolve(req.result); };
                            req.onerror = function () { return reject(req.error); };
                        })];
            }
        });
    });
}
function idbSet(key, value) {
    return __awaiter(this, void 0, void 0, function () {
        var db;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, openHandleStore()];
                case 1:
                    db = _a.sent();
                    return [2 /*return*/, new Promise(function (resolve, reject) {
                            var tx = db.transaction(STORE_NAME, 'readwrite');
                            tx.objectStore(STORE_NAME).put(value, key);
                            tx.oncomplete = function () { return resolve(); };
                            tx.onerror = function () { return reject(tx.error); };
                        })];
            }
        });
    });
}
var sanitize = function (name) { return name.replace(/[\\/:]/g, '-'); };
// Checked means "the server-side sync_state wants this on the device" -
// request_sync (queued, not copied yet) or synced (already there). Not
// checked covers both "never requested" and request_remove (unticked a
// synced item - it's still physically on the device until the next sync
// run actually deletes it, but the target state is "don't want it").
var isWanted = function (item) { return item.sync_state === 'request_sync' || item.sync_state === 'synced'; };
var STATE_BADGE = {
    synced: { label: 'on device', className: 'bg-green-100 text-green-800' },
    request_sync: { label: 'queued to sync', className: 'bg-indigo-100 text-indigo-800' },
    request_remove: { label: 'queued to remove', className: 'bg-amber-100 text-amber-800' },
};
var NOT_QUEUED_BADGE = { label: 'not queued', className: 'bg-gray-100 text-gray-600' };
export var Sync = function () {
    var _a = useState([]), items = _a[0], setItems = _a[1];
    var _b = useState(''), status = _b[0], setStatus = _b[1];
    var _c = useState(null), selectedTag = _c[0], setSelectedTag = _c[1];
    var supported = typeof window.showDirectoryPicker === 'function';
    // Every item with a file, whatever its sync_state - so you can see
    // what's already on the device (and untick it) as well as what's queued,
    // not just what's still pending.
    var loadAll = function () {
        api
            .listItems()
            .then(function (all) { return setItems(all.filter(function (i) { return i.file_type; })); })
            .catch(function (e) { return setStatus(e.message); });
    };
    useEffect(function () {
        loadAll();
    }, []);
    var toggleWanted = function (item) { return __awaiter(void 0, void 0, void 0, function () {
        var updated_1, _a, e_1;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 5, , 6]);
                    if (!isWanted(item)) return [3 /*break*/, 2];
                    return [4 /*yield*/, api.cancelSync(item.id)];
                case 1:
                    _a = _b.sent();
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, api.requestSync(item.id)];
                case 3:
                    _a = _b.sent();
                    _b.label = 4;
                case 4:
                    updated_1 = _a;
                    setItems(function (prev) { return prev.map(function (i) { return (i.id === updated_1.id ? updated_1 : i); }); });
                    return [3 /*break*/, 6];
                case 5:
                    e_1 = _b.sent();
                    setStatus(e_1.message);
                    return [3 /*break*/, 6];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    var getDirectoryHandle = function () { return __awaiter(void 0, void 0, void 0, function () {
        var saved, granted, _a, handle;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!window.showDirectoryPicker)
                        return [2 /*return*/, null];
                    return [4 /*yield*/, idbGet(HANDLE_KEY).catch(function () { return undefined; })];
                case 1:
                    saved = _b.sent();
                    if (!saved) return [3 /*break*/, 5];
                    return [4 /*yield*/, saved.queryPermission({ mode: 'readwrite' })];
                case 2:
                    _a = (_b.sent()) === 'granted';
                    if (_a) return [3 /*break*/, 4];
                    return [4 /*yield*/, saved.requestPermission({ mode: 'readwrite' })];
                case 3:
                    _a = (_b.sent()) === 'granted';
                    _b.label = 4;
                case 4:
                    granted = _a;
                    if (granted)
                        return [2 /*return*/, saved];
                    _b.label = 5;
                case 5: return [4 /*yield*/, window.showDirectoryPicker({ mode: 'readwrite' })];
                case 6:
                    handle = _b.sent();
                    return [4 /*yield*/, idbSet(HANDLE_KEY, handle)];
                case 7:
                    _b.sent();
                    return [2 /*return*/, handle];
            }
        });
    }); };
    // sync_state="synced" only ever records "we copied this file at some
    // point" - there's no ongoing link to what's actually still on the
    // device, so if the user deletes a file directly on the Kobo, the app
    // has no way to know unless it actually looks. This checks every item we
    // think is synced against the real directory contents and acks any
    // whose file is genuinely gone as "removed", so it goes back to
    // not-queued instead of the app trusting a stale state forever.
    var reconcileSynced = function (koboDir) { return __awaiter(void 0, void 0, void 0, function () {
        var synced, cleared, _i, synced_1, item, filename, e_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, api.listItems({ sync_state: 'synced' }).then(function (all) { return all.filter(function (i) { return i.file_type; }); })];
                case 1:
                    synced = _a.sent();
                    cleared = 0;
                    _i = 0, synced_1 = synced;
                    _a.label = 2;
                case 2:
                    if (!(_i < synced_1.length)) return [3 /*break*/, 8];
                    item = synced_1[_i];
                    filename = sanitize(item.title + "." + item.file_type);
                    _a.label = 3;
                case 3:
                    _a.trys.push([3, 5, , 7]);
                    return [4 /*yield*/, koboDir.getFileHandle(filename)];
                case 4:
                    _a.sent();
                    return [3 /*break*/, 7];
                case 5:
                    e_2 = _a.sent();
                    return [4 /*yield*/, api.ackSync(item.id, 'removed')];
                case 6:
                    _a.sent();
                    cleared += 1;
                    return [3 /*break*/, 7];
                case 7:
                    _i++;
                    return [3 /*break*/, 2];
                case 8: return [2 /*return*/, cleared];
            }
        });
    }); };
    var syncToKobo = function () { return __awaiter(void 0, void 0, void 0, function () {
        var dir, e_3, koboDir, reconciled, all, toSync, toRemove, _i, toRemove_1, item, filename, e_4, _a, toSync_1, item, blob, filename, fileHandle, writable, e_5;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setStatus('Requesting access to your Kobo drive...');
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, getDirectoryHandle()];
                case 2:
                    dir = _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    e_3 = _b.sent();
                    setStatus("Could not access the drive: " + e_3.message);
                    return [2 /*return*/];
                case 4:
                    if (!dir) {
                        setStatus('This browser does not support the File System Access API. Use Chrome or Edge.');
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, dir.getDirectoryHandle('rigormining', { create: true })];
                case 5:
                    koboDir = _b.sent();
                    setStatus("Checking what's actually still on the device...");
                    return [4 /*yield*/, reconcileSynced(koboDir)];
                case 6:
                    reconciled = _b.sent();
                    if (reconciled > 0) {
                        setStatus(reconciled + " item" + (reconciled === 1 ? '' : 's') + " no longer on the device - un-queued.");
                    }
                    return [4 /*yield*/, api.listItems().then(function (res) { return res.filter(function (i) { return i.file_type; }); })];
                case 7:
                    all = _b.sent();
                    toSync = all.filter(function (i) { return i.sync_state === 'request_sync'; });
                    toRemove = all.filter(function (i) { return i.sync_state === 'request_remove'; });
                    setItems(all);
                    if (toSync.length === 0 && toRemove.length === 0) {
                        setStatus('Nothing queued to sync or remove.');
                        return [2 /*return*/];
                    }
                    _i = 0, toRemove_1 = toRemove;
                    _b.label = 8;
                case 8:
                    if (!(_i < toRemove_1.length)) return [3 /*break*/, 14];
                    item = toRemove_1[_i];
                    setStatus("Removing \"" + item.title + "\"...");
                    _b.label = 9;
                case 9:
                    _b.trys.push([9, 12, , 13]);
                    filename = sanitize(item.title + "." + item.file_type);
                    return [4 /*yield*/, koboDir.removeEntry(filename).catch(function () { return undefined; })];
                case 10:
                    _b.sent(); // already gone is fine
                    return [4 /*yield*/, api.ackSync(item.id, 'removed')];
                case 11:
                    _b.sent();
                    return [3 /*break*/, 13];
                case 12:
                    e_4 = _b.sent();
                    setStatus("Failed removing \"" + item.title + "\": " + e_4.message);
                    return [2 /*return*/];
                case 13:
                    _i++;
                    return [3 /*break*/, 8];
                case 14:
                    _a = 0, toSync_1 = toSync;
                    _b.label = 15;
                case 15:
                    if (!(_a < toSync_1.length)) return [3 /*break*/, 25];
                    item = toSync_1[_a];
                    setStatus("Copying \"" + item.title + "\"...");
                    _b.label = 16;
                case 16:
                    _b.trys.push([16, 23, , 24]);
                    return [4 /*yield*/, api.fetchFileBlob(item.id)];
                case 17:
                    blob = _b.sent();
                    filename = sanitize(item.title + "." + item.file_type);
                    return [4 /*yield*/, koboDir.getFileHandle(filename, { create: true })];
                case 18:
                    fileHandle = _b.sent();
                    return [4 /*yield*/, fileHandle.createWritable()];
                case 19:
                    writable = _b.sent();
                    return [4 /*yield*/, writable.write(blob)];
                case 20:
                    _b.sent();
                    return [4 /*yield*/, writable.close()];
                case 21:
                    _b.sent();
                    return [4 /*yield*/, api.ackSync(item.id, 'synced')];
                case 22:
                    _b.sent();
                    return [3 /*break*/, 24];
                case 23:
                    e_5 = _b.sent();
                    setStatus("Failed on \"" + item.title + "\": " + e_5.message);
                    return [2 /*return*/];
                case 24:
                    _a++;
                    return [3 /*break*/, 15];
                case 25:
                    setStatus("Done - copied " + toSync.length + " item" + (toSync.length === 1 ? '' : 's') + " and removed " + toRemove.length + " item" + (toRemove.length === 1 ? '' : 's') + ".");
                    loadAll();
                    return [2 /*return*/];
            }
        });
    }); };
    var visibleItems = items.filter(function (item) {
        if (selectedTag === null)
            return true;
        if (selectedTag === UNTAGGED)
            return item.tags.length === 0;
        return item.tags.some(function (t) { return t.name === selectedTag; });
    });
    var queuedSync = items.filter(function (i) { return i.sync_state === 'request_sync'; }).length;
    var queuedRemove = items.filter(function (i) { return i.sync_state === 'request_remove'; }).length;
    var onDevice = items.filter(function (i) { return i.sync_state === 'synced'; }).length;
    return (React.createElement(React.Fragment, null,
        React.createElement(Navigation, { loggedIn: true }),
        React.createElement("div", { className: "flex", style: { height: 'calc(100vh - 4rem)' } },
            React.createElement(TagTree, { items: items, selected: selectedTag, onSelect: setSelectedTag }),
            React.createElement("div", { className: "flex-1 overflow-y-auto" },
                React.createElement("div", { className: "max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" },
                    React.createElement("h1", { className: "text-lg font-semibold text-gray-900 mb-4" }, "Sync to Kobo"),
                    !supported && (React.createElement("p", { className: "text-sm text-red-700 mb-4" }, "This browser doesn't support one-click sync (Chrome or Edge required). Download items individually from their detail page and copy them onto your Kobo over USB instead.")),
                    React.createElement("p", { className: "text-sm text-gray-600 mb-4" },
                        onDevice,
                        " on device, ",
                        queuedSync,
                        " queued to sync, ",
                        queuedRemove,
                        " queued to remove."),
                    supported && (React.createElement("button", { onClick: syncToKobo, disabled: queuedSync === 0 && queuedRemove === 0, className: "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50" }, "Plug in your Kobo, then click to sync")),
                    status && React.createElement("p", { className: "mt-4 text-sm text-gray-700" }, status),
                    React.createElement("ul", { className: "mt-6 divide-y divide-gray-100 border border-gray-200 rounded-md bg-white" },
                        visibleItems.map(function (item) {
                            var badge = (item.sync_state && STATE_BADGE[item.sync_state]) || NOT_QUEUED_BADGE;
                            return (React.createElement("li", { key: item.id, className: "px-3 py-2 flex justify-between items-center gap-3" },
                                React.createElement("label", { className: "flex items-center gap-3 flex-1 min-w-0 cursor-pointer" },
                                    React.createElement("input", { type: "checkbox", checked: isWanted(item), onChange: function () { return toggleWanted(item); }, className: "rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" }),
                                    React.createElement("span", { className: "truncate text-sm text-gray-900" }, item.title)),
                                React.createElement("span", { className: "text-xs px-2 py-0.5 rounded-full flex-shrink-0 " + badge.className }, badge.label)));
                        }),
                        visibleItems.length === 0 && (React.createElement("li", { className: "px-3 py-6 text-center text-sm text-gray-500" }, "No items here."))))))));
};
export default Sync;
